import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { prefetchTeacherMeDashboard } from '@/features/academics/hooks/queries/useTeacherDashboardData'
import { accountsApi, parentInviteApi, profileApi } from '../../services/api'
import { isUserProfileComplete } from '../../utils/profileCompletion'
import type {
  LoginRequest,
  ParentEmailVerificationRequest,
  ParentEmailVerificationResponse,
  ParentInviteAcceptRequest,
  ParentRegistrationRequest,
  ParentRegistrationResponse,
} from '../../types/api'
import type { UserDetails } from '../../types/models'
import { useAuthStore } from '../../../../stores/authStore'

function getDefaultTeacherSchoolId(profile: UserDetails): string | null {
  const activeAssignments = (profile.school_assignments ?? []).filter(
    assignment => assignment.is_active,
  )
  const primary = activeAssignments.find(assignment => assignment.is_primary)
  return primary?.school_id ?? activeAssignments[0]?.school_id ?? null
}

/**
 * Teacher `/me/*` routes (except classes) require `school_year_id`. Resolve it from
 * `/me/classes/?school_id=` then warm the rest of the dashboard cache.
 */
async function prefetchTeacherDashboard(queryClient: QueryClient, profile: UserDetails) {
  if (profile.role !== 'TEACHER') return

  const schoolId = getDefaultTeacherSchoolId(profile)
  if (!schoolId) return

  await prefetchTeacherMeDashboard(queryClient, schoolId)
}

export function useLogin() {
  const queryClient = useQueryClient()
  const setAuth = useAuthStore(s => s.setAuth)
  const setUser = useAuthStore(s => s.setUser)
  const setProfileComplete = useAuthStore(s => s.setProfileComplete)
  const clearAuth = useAuthStore(s => s.clearAuth)

  return useMutation({
    mutationFn: async (data: LoginRequest) => {
      try {
        const tokens = await accountsApi.login(data)
        setAuth(tokens)

        try {
          const profile = await profileApi.getProfile()
          setUser(profile)
          setProfileComplete(isUserProfileComplete(profile))
          void prefetchTeacherDashboard(queryClient, profile)
        } catch {
          // If profile fetch fails, clear auth and rethrow
          clearAuth()
          throw new Error('Failed to load user profile. Please try again.')
        }

        return tokens
      } catch (error) {
        clearAuth()
        throw error
      }
    },
  })
}

export function useRegisterParent() {
  return useMutation<ParentRegistrationResponse, unknown, ParentRegistrationRequest>({
    mutationFn: data => accountsApi.registerParent(data),
  })
}

/** Ask for a new verification link; the server answers the same way for any email. */
export function useResendParentVerification() {
  return useMutation<{ success: boolean; message: string }, unknown, { email: string }>({
    mutationFn: ({ email }) => accountsApi.resendParentVerification(email),
  })
}

export function useVerifyParentEmail() {
  const setAuth = useAuthStore(s => s.setAuth)
  const setUser = useAuthStore(s => s.setUser)
  const setProfileComplete = useAuthStore(s => s.setProfileComplete)
  const clearAuth = useAuthStore(s => s.clearAuth)

  return useMutation<ParentEmailVerificationResponse, unknown, ParentEmailVerificationRequest>({
    mutationFn: data => accountsApi.verifyParentEmail(data),
    onSuccess: async tokens => {
      try {
        setAuth(tokens)
      } catch (authErr) {
        clearAuth()
        throw authErr
      }

      try {
        const profile = await profileApi.getProfile()
        setUser(profile)
        setProfileComplete(isUserProfileComplete(profile))
      } catch {
        clearAuth()
        throw new Error('Failed to load user profile. Please try again.')
      }
    },
    onError: () => {
      clearAuth()
    },
  })
}

/** Accept an imported family's invite: sets the password, then signs the parent in. */
export function useAcceptParentInvite() {
  const setAuth = useAuthStore(s => s.setAuth)
  const setUser = useAuthStore(s => s.setUser)
  const setProfileComplete = useAuthStore(s => s.setProfileComplete)
  const clearAuth = useAuthStore(s => s.clearAuth)

  return useMutation<ParentEmailVerificationResponse, unknown, ParentInviteAcceptRequest>({
    mutationFn: data => parentInviteApi.accept(data),
    onSuccess: async tokens => {
      setAuth(tokens)
      try {
        const profile = await profileApi.getProfile()
        setUser(profile)
        setProfileComplete(isUserProfileComplete(profile))
      } catch {
        clearAuth()
        throw new Error(
          'Your account is ready, but signing in failed. Sign in with your new password.',
        )
      }
    },
  })
}

export function useLogout() {
  const clearAuth = useAuthStore(s => s.clearAuth)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      // Read at send time: a refresh just before logout rotates the token.
      const { refreshToken } = useAuthStore.getState()
      if (!refreshToken) {
        return { success: true as const, message: 'Logged out' }
      }
      return accountsApi.logout({ refresh_token: refreshToken })
    },
    onSuccess: () => {
      clearAuth()
      qc.clear()
    },
    onError: () => {
      // Even if logout fails on server, clear local state
      clearAuth()
      qc.clear()
    },
  })
}
